import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, Plus, Search, Globe2, Shield, Verified, Crown, TrendingUp } from "lucide-react";

const CATEGORY_LABELS = {
  ma: "M&A", fleet: "Fleet", aviation: "Aviation", maritime: "Maritime",
  "real-estate": "Real Estate", energy: "Energy", capital: "Capital",
  partnership: "Partnership", talent: "Talent", distressed: "Distressed",
};

function ProfileChip({ p }) {
  return (
    <div className="flex items-center gap-3" data-testid={`profile-chip-${p.id || p.handle}`}>
      <div
        className="w-10 h-10 bg-[#064E3B] text-white flex items-center justify-center font-bold text-sm shrink-0"
        style={{ background: `hsl(${(p.avatar_seed || p.name || "x").charCodeAt(0) * 5 % 360}, 35%, 22%)` }}
      >
        {(p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
      </div>
      <div className="leading-tight">
        <div className="font-semibold text-sm text-[#064E3B] flex items-center gap-1.5">
          {p.name}
          {p.verified && <Verified className="w-3.5 h-3.5 text-[#064E3B]" />}
          {p.premium && <Crown className="w-3.5 h-3.5 text-[#FACC15]" />}
        </div>
        <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">{p.title || "Member"} · {p.company}</div>
      </div>
    </div>
  );
}

function ListingCard({ l }) {
  return (
    <Link to={`/exchange/listing/${l.id}`} className="bento-card p-7 flex flex-col gap-4" data-testid={`listing-${l.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-[#064E3B] text-white">{CATEGORY_LABELS[l.category] || l.category}</span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[#D97706] text-[#D97706]">{l.type}</span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-[#FACC15] text-[#064E3B] font-bold">{l.size}</span>
        </div>
        <span className="mono text-[10px] tracking-wider text-gray-500 uppercase shrink-0">{l.region}</span>
      </div>
      <h3 className="display-black text-xl uppercase text-[#064E3B] leading-tight">{l.title}</h3>
      <div className="mono text-[12px] text-[#D97706] uppercase tracking-wider">{l.headline}</div>
      <p className="text-sm text-gray-700 flex-1">{l.body.slice(0, 180)}{l.body.length > 180 ? "…" : ""}</p>
      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <ProfileChip p={l.posted_by_profile} />
        <div className="text-right shrink-0">
          <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Interest</div>
          <div className="display-black text-lg text-[#D97706]">{l.interest_count}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Exchange() {
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [meta, setMeta] = useState({ categories: [], sizes: [], types: [] });
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ category: "", type: "", size: "", region: "" });
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/exchange/stats").then(r => setStats(r.data));
    api.get("/exchange/categories").then(r => setMeta(r.data));
    api.get("/exchange/members").then(r => setMembers(r.data.slice(0, 8)));
  }, []);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries({ ...filters, q }).filter(([_, v]) => v));
    api.get("/exchange/listings", { params }).then(r => setListings(r.data));
  }, [filters, q]);

  return (
    <>
      {/* Hero */}
      <section className="bg-[#064E3B] text-white" data-testid="exchange-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ THE AVAILABLE EXCHANGE · WORLDWIDE B2B DEAL NETWORK
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-5xl">
            Where the big deals<br /><span className="text-[#FACC15]">live.</span>
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-3xl">
            A premium B2B exchange for $1M+ deals. Acquisitions. Fleet liquidations. Capital. Talent. Real property. Verified operators only — worldwide.
          </p>

          {stats && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/15" data-testid="exchange-stats">
              {[
                { v: `$${(stats.total_deal_value_m / 1000).toFixed(2)}B+`, l: "Listed Value" },
                { v: stats.listings, l: "Active Deals" },
                { v: stats.members, l: "Verified Members" },
                { v: stats.regions, l: "Regions" },
              ].map(s => (
                <div key={s.l} className="bg-[#064E3B] p-6 text-center">
                  <div className="display-black text-3xl lg:text-4xl text-[#FACC15]">{s.v}</div>
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-white/70 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/exchange/post" className="btn-signal" data-testid="exchange-post-cta">
              <Plus className="w-4 h-4" /> Post a Deal
            </Link>
            <Link to="/exchange/honor" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }} data-testid="exchange-honor-cta">
              Honor Pay
            </Link>
            <Link to="/exchange/members" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }} data-testid="exchange-members-cta">
              Members
            </Link>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section className="border-b border-gray-300 bg-white sticky top-16 z-20" data-testid="exchange-toolbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search deals — keywords, geography…" className="input-federal pl-9" data-testid="exchange-search" />
          </div>
          <select className="input-federal max-w-[180px]" value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} data-testid="filter-category">
            <option value="">All Categories</option>
            {meta.categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select className="input-federal max-w-[160px]" value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})} data-testid="filter-type">
            <option value="">All Types</option>
            {meta.types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="input-federal max-w-[140px]" value={filters.size} onChange={e => setFilters({...filters, size: e.target.value})} data-testid="filter-size">
            <option value="">All Sizes</option>
            {meta.sizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="input-federal max-w-[160px]" value={filters.region} onChange={e => setFilters({...filters, region: e.target.value})} data-testid="filter-region">
            <option value="">Worldwide</option>
            {["North America", "South America", "Europe", "Middle East", "Asia", "Africa", "Oceania", "Global"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-4 gap-10">
          {/* Feed */}
          <div className="lg:col-span-3" data-testid="exchange-feed">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Active Listings</div>
                <h2 className="display-black text-3xl uppercase text-[#064E3B] mt-1">{listings.length} deals matched.</h2>
              </div>
              <Link to="/exchange/post" className="btn-federal hidden md:inline-flex"><Plus className="w-4 h-4" /> Post</Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-300 border border-gray-300">
              {listings.map(l => <ListingCard key={l.id} l={l} />)}
              {listings.length === 0 && <div className="bg-white p-10 col-span-2 text-center text-gray-500">No deals match these filters. Try widening your scope.</div>}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-8" data-testid="exchange-sidebar">
            <div className="border border-gray-300 p-6 bg-gray-50">
              <div className="flex items-center gap-2">
                <Globe2 className="w-4 h-4 text-[#D97706]" />
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Why this beats X</div>
              </div>
              <h3 className="display-black text-lg uppercase text-[#064E3B] mt-2">No tire-kickers.</h3>
              <ul className="mt-4 space-y-2 text-sm text-gray-800">
                <li className="flex gap-2"><Shield className="w-4 h-4 text-[#064E3B] shrink-0 mt-0.5" /> $1M floor — no small deals</li>
                <li className="flex gap-2"><Verified className="w-4 h-4 text-[#064E3B] shrink-0 mt-0.5" /> Verified members only</li>
                <li className="flex gap-2"><Crown className="w-4 h-4 text-[#FACC15] shrink-0 mt-0.5" /> Premium tier unlocks NDAs + data rooms</li>
                <li className="flex gap-2"><TrendingUp className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" /> Honor Pay — pledge a slice when deals close</li>
              </ul>
              <Link to="/exchange/honor" className="btn-ghost w-full mt-5 justify-center" data-testid="sidebar-honor">Learn Honor Pay</Link>
              <Link to="/exchange/closed-deals" className="btn-federal w-full mt-3 justify-center" data-testid="sidebar-closed-wall">Closed Deals Wall <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>

            <div className="border border-gray-300 p-6">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Top Members</div>
              <div className="mt-4 space-y-4">
                {members.map(m => (
                  <Link key={m.id} to={`/exchange/member/${m.handle}`} className="block hover:bg-gray-50 -mx-2 px-2 py-2" data-testid={`sidebar-member-${m.handle}`}>
                    <ProfileChip p={m} />
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase mt-1 pl-13">
                      {m.deals_closed} deals · ${m.deal_volume_m}M closed
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/exchange/members" className="mono text-[11px] tracking-wider uppercase text-[#D97706] flex items-center gap-2 mt-5">
                All members <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="border border-[#064E3B] bg-[#064E3B] text-white p-6">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#FACC15]">// Premium — Coming Soon</div>
              <h3 className="display-black text-xl uppercase mt-2">Unlock the war chest.</h3>
              <p className="text-sm text-white/80 mt-3">
                NDA library · embedded data rooms · direct introductions · escrow rails · AI deal scoring.
              </p>
              <button disabled className="btn-ghost mt-5 opacity-60 cursor-not-allowed" style={{ color: "#FACC15", borderColor: "#FACC15" }} data-testid="premium-coming-soon">
                Waitlist Premium
              </button>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
